import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import classNames from 'classnames'
import { makeStyles } from '@mui/styles'
import { generatePath, useNavigate } from 'react-router'
import { useTrackHomeEnterpriseEvent } from '../../hooks/useTrackHomeEnterpriseEvent'
import { HOME_SECTION_TYPE } from './home-sections'
import { useFormFactor } from '../../../../hooks'
import { routes } from '../../../../router/routes'

const cardShape = 'polygon(25px 0, 100% 0, 100% calc(100% - 25px), calc(100% - 25px) 100%, 0 100%, 0 25px);'

const promoteFeatureBannersConfig = {
  playlists: {
    id: 'playlists',
    title: 'Create Personalized Learning Playlists',
    description: 'Start building your own playlists today to take your learning to the next level.',
    ctaText: 'See all Playlists',
    ctaLink: routes.playlists.path,
    imageUrl: 'https://nova-live.imgix.net//home-page-banners/playlists.png?fit=crop&w=270&h=190'
  },
  myLearning: {
    id: 'myLearning',
    title: 'Track Your Learning Progress',
    description: 'Instantly see your total learning time, top topics and resources—all in one personal dashboard.',
    ctaText: 'View My Learning',
    ctaLink: routes.myLearning.path,
    imageUrl: 'https://nova-live.imgix.net//home-page-banners/myLearning.png?fit=crop&w=270&h=190'
  },
  msa: {
    id: 'msa',
    title: 'Benchmark Your Management Skills',
    description: 'Unlock clear, research-backed insights into managerial strengths and areas for growth.',
    ctaText: 'Begin your Assessment',
    ctaLink: generatePath(routes['assessment-details'].path, { assessmentId: 'manager-skill-assessment' }),
    imageUrl: 'https://nova-live.imgix.net//home-page-banners/msa.png?fit=crop&w=270&h=190'
  },
  bookmarks: {
    id: 'bookmarks',
    title: 'Save It for Later',
    description: 'Bookmark any article, video or tool—your personal reading list is always one click away.',
    ctaText: 'View Bookmarks',
    ctaLink: routes.bookmarks.path,
    imageUrl: 'https://nova-live.imgix.net//home-page-banners/bookmarks.png?fit=crop&w=270&h=190'
  }
}

const useStyles = makeStyles(({ palette, spacing, typography }) => ({
  verticalBannerCtn: {
    width: '100%',
    padding: spacing(3),
    backgroundColor: ({ bg }) => bg
  },
  verticalBannerTxtCtn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  verticalBannerTitle: {
    color: ({ text }) => text,
    fontWeight: 600,
    ...typography.heading.h5
  },
  verticalBannerDescription: {
    color: ({ text }) => text,
    marginTop: spacing(2),
    ...typography.body.m,
    marginBottom: spacing(2)
  },
  verticalBannerImg: {
    height: '100%',
    width: ({ isMobile }) => isMobile ? '100%' : 150,
    maxHeight: 150,
    objectFit: 'cover',
    borderRadius: '8px'
  },
  verticalBannerCard: {
    clipPath: cardShape,
    minHeight: '200px',
    height: '100%',
    display: 'flex',
    gap: spacing(2),
    flexDirection: ({ isMobile }) => isMobile ? 'column' : 'row',
    borderRadius: 0,
    width: '100%',
    backgroundColor: ({ bg }) => bg
  },
  verticalBannerEdge: {
    paddingRight: 1,
    paddingBottom: 1,
    clipPath: cardShape,
    minHeight: '200px',
    height: '100%',
    width: '100%',
    backgroundColor: ({ bg }) => bg
  }
}))

export const PromoteFeatureBanner = ({ id, title, description, imageUrl, ctaText, ctaLink }) => {
  const { isMobile } = useFormFactor()
  const classes = useStyles({ bg: '#e4edf5', text: '#333', isMobile })
  const navigate = useNavigate()
  const { trackClickEvent } = useTrackHomeEnterpriseEvent()

  const handleCtaClick = () => {
    trackClickEvent(HOME_SECTION_TYPE.PROMOTE_FEATURE, {
      featureId: id,
      featureTitle: title
    })
    navigate(ctaLink)
  }

  return (
    <Box className={classes.verticalBannerEdge} data-test={`home-promote-feature-${id}`}>
      <Box className={classNames(classes.verticalBannerCtn, classes.verticalBannerCard)} d>
        <Box>
          <img src={imageUrl} className={classes.verticalBannerImg} />
        </Box>
        <Box className={classes.verticalBannerTxtCtn}>
          <Box>
            <Typography className={classes.verticalBannerTitle} variant='h5' data-test={`home-promote-feature-${id}-title`}>{title}</Typography>
            <Typography
              variant='body2'
              className={classes.verticalBannerDescription}
              data-test={`home-promote-feature-${id}-description`}
            >
              {description}
            </Typography>
          </Box>
          <Box>
            <Button variant='contained' color='white' onClick={handleCtaClick} data-test={`home-promote-feature-${id}-cta`}>
              {ctaText}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const PromoteFeaturesRails = ({ promoteFeaturesSection }) => {
  const { items } = promoteFeaturesSection
  const { isMobile, isSmallTablet } = useFormFactor()
  const itemsConfig = items.map(item => promoteFeatureBannersConfig[item]).filter(Boolean)

  return (
    <Box
      data-test='home-promote-features-rails'
      sx={{
        display: 'flex',
        flexDirection: isMobile || isSmallTablet ? 'column' : 'row',
        gap: 2,
        flexWrap: 'wrap'
      }}
    >
      {itemsConfig.map((item, itemIndex) => (
        <Box
          key={item.id}
          id={item.id}
          sx={{
            flex: 1
          }}
        >
          <PromoteFeatureBanner
            title={item.title}
            id={item.id}
            description={item.description}
            imageUrl={item.imageUrl}
            ctaText={item.ctaText}
            ctaLink={item.ctaLink}
          />
        </Box>
      ))}
    </Box>
  )
}
